import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu, MenuItem,
  Chip, Collapse, useMediaQuery, useTheme
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, People as PeopleIcon,
  Business as BusinessIcon, Campaign as CampaignIcon, Settings as SettingsIcon,
  Logout as LogoutIcon, AccountCircle, SupervisorAccount as UsersIcon,
  Analytics as AnalyticsIcon, Receipt as ConversionsIcon, AddBox as ImportIcon,
  ExpandLess, ExpandMore, BarChart as ReportIcon, Facebook as FacebookIcon,
  Leaderboard as FbDashboardIcon, ListAlt as FbLeadsIcon, TrendingUp as FbAnalyticsIcon,
  Email as DemIcon, CalendarMonth, Storage, Analytics, Send, Close as CloseIcon, ViewList
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import TermsAcceptanceDialog from './TermsAcceptanceDialog';

const drawerWidth = 260;

function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [facebookOpen, setFacebookOpen] = useState(false);
  const [demOpen, setDemOpen] = useState(false);
  const [branding, setBranding] = useState({ logo_url: '', company_name: 'Trackoo' });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isManager } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsChecking, setTermsChecking] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  useEffect(() => {
    checkTermsAcceptance();
  }, []);

  const checkTermsAcceptance = async () => {
    try {
      const res = await axios.get('/admin/terms-status');
      setTermsAccepted(res.data.accepted);
    } catch (e) {
      console.error('Failed to check terms status');
      setTermsAccepted(true);
    } finally {
      setTermsChecking(false);
    }
  };

  const handleAcceptTerms = async () => {
    setTermsLoading(true);
    try {
      await axios.post('/admin/accept-terms');
      setTermsAccepted(true);
    } catch (e) {
      console.error('Failed to accept terms');
    } finally {
      setTermsLoading(false);
    }
  };

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const fetchBranding = async () => {
    try {
      const res = await axios.get('/admin/branding');
      if (res.data.data) {
        setBranding({
          logo_url: res.data.data.logo_url || '',
          company_name: res.data.data.company_name || 'Trackoo'
        });
      }
    } catch (e) { console.error('Failed to fetch branding'); }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); navigate('/login'); };

  const mainMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ['admin', 'manager', 'partner'] },
    { text: 'Partners', icon: <PeopleIcon />, path: '/partners', roles: ['admin', 'manager'] },
    { text: 'Advertisers', icon: <BusinessIcon />, path: '/advertisers', roles: ['admin'] },
    { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns', roles: ['admin', 'manager', 'partner'] },
  ];

  const analyzeMenuItems = [
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics', roles: ['admin', 'manager', 'partner'] },
    { text: 'Conversions', icon: <ConversionsIcon />, path: '/conversions', roles: ['admin', 'manager', 'partner'] },
    { text: 'Import', icon: <ImportIcon />, path: '/import-conversion', roles: ['admin', 'manager'] },
  ];

  const facebookMenuItems = [
    { text: 'FB Dashboard', icon: <FbDashboardIcon />, path: '/facebook', roles: ['admin', 'media_buyer'] },
    { text: 'FB Campaigns', icon: <CampaignIcon />, path: '/facebook/campaigns', roles: ['admin', 'media_buyer'] },
    { text: 'FB Leads', icon: <FbLeadsIcon />, path: '/facebook/leads', roles: ['admin'] },
    { text: 'FB Analytics', icon: <FbAnalyticsIcon />, path: '/facebook/analytics', roles: ['admin', 'media_buyer'] },
    { text: 'FB Settings', icon: <SettingsIcon />, path: '/facebook/settings', roles: ['admin'] },
  ];

  const demMenuItems = [
    { text: 'Calendar', icon: <CalendarMonth />, path: '/dem/calendar', roles: ['admin', 'manager'] },
    { text: 'Analytics', icon: <Analytics />, path: '/dem/analytics', roles: ['admin', 'manager'] },
    { text: 'Databases', icon: <Storage />, path: '/dem/databases', roles: ['admin', 'manager'] },
    { text: 'Maildome', icon: <Send />, path: '/dem/maildome', roles: ['admin', 'manager'] },
    { text: 'Virtual Lists', icon: <ViewList />, path: '/dem/virtual-lists', roles: ['admin', 'manager'] },
  ];

  const bottomMenuItems = [
    { text: 'Users', icon: <UsersIcon />, path: '/users', roles: ['admin'] },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['admin', 'manager', 'partner'] },
  ];

  const filterByRole = (items) => items.filter(item => item.roles.includes(user?.role));
  const getRoleColor = () => {
    if (isAdmin()) return '#10B981';
    if (isManager()) return '#F59E0B';
    if (user?.role === 'media_buyer') return '#3B82F6';
    return '#6B7280';
  };
  const getRoleLabel = () => {
    if (isAdmin()) return 'Admin';
    if (isManager()) return 'Manager';
    if (user?.role === 'media_buyer') return 'Media Buyer';
    return 'Partner';
  };

  const renderMenuItem = (item) => (
    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        component={Link}
        to={item.path}
        selected={location.pathname === item.path}
        sx={{
          borderRadius: 2, mx: 1, py: 1.25,
          textDecoration: 'none',
          '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } },
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
        }}
      >
        <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: location.pathname === item.path ? 600 : 400, color: 'white' }} />
      </ListItemButton>
    </ListItem>
  );

  const getLogoUrl = () => {
    if (!branding.logo_url) return null;
    if (branding.logo_url.startsWith('/uploads')) {
      const baseUrl = axios.defaults.baseURL || '';
      return baseUrl.replace('/api', '') + branding.logo_url;
    }
    return branding.logo_url;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#4869EE' }}>
      <Box sx={{ p: 2.5, textAlign: 'center', position: 'relative' }}>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        )}
        {branding.logo_url ? (
          <img src={getLogoUrl()} alt={branding.company_name} style={{ height: 36, maxWidth: 160, objectFit: 'contain', marginBottom: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>{branding.company_name}</Typography>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', display: 'block' }}>Performance Marketing Platform</Typography>
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)', mx: 2 }} />

      <List sx={{ px: 0.5, flexGrow: 1, mt: 1, overflowY: 'auto' }}>
        {filterByRole(mainMenuItems).map(renderMenuItem)}

        {/* ANALYZE Section */}
        {user?.role !== 'media_buyer' && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton onClick={() => setAnalyzeOpen(!analyzeOpen)} sx={{ borderRadius: 2, mx: 1, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}><ReportIcon /></ListItemIcon>
                 <ListItemText primary="Analyze" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, color: 'white' }} />
                {analyzeOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={analyzeOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {filterByRole(analyzeMenuItems).map(renderMenuItem)}
              </List>
            </Collapse>
          </>
        )}

        {/* FACEBOOK Section */}
        {(user?.role === 'admin' || user?.role === 'media_buyer') && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton onClick={() => setFacebookOpen(!facebookOpen)} sx={{ borderRadius: 2, mx: 1, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}><FacebookIcon /></ListItemIcon>
                 <ListItemText primary="Facebook" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, color: 'white' }} />
                {facebookOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={facebookOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {filterByRole(facebookMenuItems).map(renderMenuItem)}
              </List>
            </Collapse>
          </>
        )}

        {/* DEM Section */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton onClick={() => setDemOpen(!demOpen)} sx={{ borderRadius: 2, mx: 1, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}><DemIcon /></ListItemIcon>
                 <ListItemText primary="DEM" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, color: 'white' }} />
                {demOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={demOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {filterByRole(demMenuItems).map(renderMenuItem)}
              </List>
            </Collapse>
          </>
        )}

        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)', mx: 2, my: 1.5 }} />
        {filterByRole(bottomMenuItems).map(renderMenuItem)}
      </List>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)', mx: 2 }} />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>Logged in as</Typography>
        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mt: 0.25, fontSize: '0.85rem' }}>{user?.full_name || user?.email}</Typography>
        <Chip label={getRoleLabel()} size="small" sx={{ mt: 1, bgcolor: getRoleColor(), color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
      </Box>
      <Box sx={{ p: 1.5, textAlign: 'center' }}>
        <Box sx={{ mb: 1 }}>
          <Typography
            component="a"
            href="/privacy"
            target="_blank"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', textDecoration: 'none', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
          >
            Privacy Policy
          </Typography>
          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem', mx: 0.5 }}>|</Typography>
          <Typography
            component="a"
            href="/cookie"
            target="_blank"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', textDecoration: 'none', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
          >
            Cookie Policy
          </Typography>
          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem', mx: 0.5 }}>|</Typography>
          <Typography
            component="a"
            href="/terms"
            target="_blank"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', textDecoration: 'none', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
          >
            Terms
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>© {new Date().getFullYear()} Digitoo Group</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0} sx={{ display: { md: 'none' }, bgcolor: '#4869EE', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Toolbar sx={{ minHeight: { xs: 56 } }}>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}><MenuIcon /></IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1rem' }}>{branding.company_name}</Typography>
          <IconButton onClick={handleMenuOpen} color="inherit">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.875rem' }}>{user?.full_name?.charAt(0)}</Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 } }}>{drawer}</Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' } }} open>{drawer}</Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', bgcolor: '#F8FAFC', pt: { xs: 7, md: 0 } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'flex-end', px: 3, py: 1.5, bgcolor: '#4869EE', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'white' }}>{user?.full_name}</Typography>
            <IconButton onClick={handleMenuOpen} size="small">
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.9rem' }}>{user?.full_name?.charAt(0)}</Avatar>
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled>
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
          <Box><Typography variant="body2">{user?.email}</Typography><Typography variant="caption" color="textSecondary">Role: {user?.role}</Typography></Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Logout</MenuItem>
      </Menu>

      <TermsAcceptanceDialog
        open={!termsChecking && !termsAccepted}
        onAccept={handleAcceptTerms}
        loading={termsLoading}
      />
    </Box>
  );
}

export default Layout;
